import { updateCard, moveCard } from '@domain/_redux/workflow/actions/actions';
import { createMiddleware } from '@shared/redux';
import { push, getLocation, goBack } from 'connected-react-router';
import Routes from '@domain/router/routes.constants';
import { cardIsAutomatic } from '@domain/_screens/workflow/card/components/card.utils';
import { showErrorToast } from '@shared/toasts/toasts.state';
import { CARD_STATES } from '@shared/constants/workflow/WorkflowCardStatusConstant';
import { isCardFromExternalLink } from '@domain/cards/card-selected/card-selected.utils';
import automaticCardNotifications from '@domain/_screens/workflow/automatic-card/render-by/automaticCardNotifications';
import Constants from '@shared/constants';
import {
    getCardSelected,
    isCardModified,
    setCardSelected,
    updateCardRequest,
    getCardOriginRouter,
    setIsCardValid,
} from '../card-selected/card-selected.state';
import {
    CLOSE_CARD_MODAL_WITH_TIMEOUT,
    CLOSE_CARD_SELECTED_MODAL,
    OPEN_CARD_SELECTED_MODAL,
    APPLY_CARD,
    APPLY_AUTOMATIC_CARD,
    UPDATE_WORKFLOW_CARD,
    closeCardModal,
    resetCardInterfaceState,
    isModalOpen,
    applyAutomaticCard,
    activeProgressBarAnimation,
    setAutomationLoading,
    closeCardModalWithTimeout,
    updateWorkflowCard,
} from './card-interface.state';
import { validateAutomaticCard } from './card-interface.utils';

export const closeCardModalWithTimeoutMiddleware = store => next => (action) => {
    const nextAction = next(action);
    const { type } = action;

    if (type === CLOSE_CARD_MODAL_WITH_TIMEOUT) {
        setTimeout(() => store.dispatch(closeCardModal()), 2500);
    }
    return nextAction;
};

export const cardInterfaceMiddleware = createMiddleware({
    [UPDATE_WORKFLOW_CARD]: (state) => {
        const originRoute = getCardOriginRouter(state);
        const isFromExternalLink = isCardFromExternalLink(originRoute);

        if (isFromExternalLink) return [];

        const cardSelected = getCardSelected(state);

        return [updateCard(cardSelected)];
    },

    [CLOSE_CARD_SELECTED_MODAL]: (state) => {
        const isModified = isCardModified(state);
        const cardSelected = getCardSelected(state);
        const originRoute = getCardOriginRouter(state);
        const isFromExternalLink = isCardFromExternalLink(originRoute);

        const requestAction = isFromExternalLink
            ? [updateCardRequest(cardSelected)]
            : [];

        const updateActions = isModified
            ? [...requestAction, updateWorkflowCard()]
            : [];

        const redirectActions = isFromExternalLink ? [push(Routes.WORKFLOW)] : [goBack()];

        return [
            ...updateActions,
            setCardSelected({ card: null }),
            resetCardInterfaceState(),
            ...redirectActions,
        ];
    },
    [OPEN_CARD_SELECTED_MODAL]: (state) => {
        const cardSelected = getCardSelected(state);
        const cardLocation = getLocation(state);
        const originRoute = getCardOriginRouter(state);
        const { id } = cardSelected;

        if (isCardFromExternalLink(originRoute)) return [];

        return [
            push(Routes.WORKFLOW_WITH_CARD_ID.replace(':cardId', id), { card: cardLocation }),
        ];
    },
    [APPLY_CARD]: (state, action) => {
        const { card } = action.payload;

        const applyAction = cardIsAutomatic(card)
            ? [applyAutomaticCard(card)]
            : [moveCard(card, CARD_STATES.DONE, 0)];

        return applyAction;
    },
    [APPLY_AUTOMATIC_CARD]: (state, action) => {
        const { card } = action.payload;
        const originRoute = getCardOriginRouter(state);
        const isExternalLink = isCardFromExternalLink(originRoute);

        try {
            validateAutomaticCard(card);
        } catch (error) {
            return [
                setIsCardValid({ isCardValid: false }),
                showErrorToast({ message: error.message }),
            ];
        }

        const newCard = {
            ...card,
            automation_status: Constants.CARD_AUTOMATION_STATUS.STATUS_SCHEDULED,
        };

        const modalIsOpenActions = isModalOpen(state)
            ? [
                setIsCardValid({ isCardValid: true }),
                activeProgressBarAnimation(),
                setAutomationLoading(),
                closeCardModalWithTimeout(),
            ]
            : [];

        const updateCardAction = isExternalLink && isModalOpen(state)
            ? [setCardSelected({
                card: { ...newCard, status: CARD_STATES.DONE },
                originRoute,
                isCardValid: true,
                cardFieldsModified: true,
            }), updateCardRequest({ ...newCard, status: CARD_STATES.DONE })]
            : [moveCard(newCard, CARD_STATES.DONE, 0)];

        return [
            ...modalIsOpenActions,
            ...updateCardAction,
            automaticCardNotifications(card),
        ];
    },

});
